<template>
  <SettingLayout ref="layout" :title="title" :subtitle="subtitle" :has-permission="hasAnyEditPermission" @cancel="cancel" @save="save">
    <div class="mt-7">
      <template v-for="section in contentOrder">

        <!-- Upgrade Button -->
        <template v-if="section === 'upgrade'">
          <v-row :key="'upgrade'" dense>
            <v-col cols="12">
              <v-btn
                dark
                color="tiffany-10"
                depressed
                :block="$vuetify.breakpoint.smAndDown"
                class="px-md-12 rounded-lg"
                href="https://lin.ee/mWs8BoE"
                target="_blank"
              >
                欲使用診所社群功能，請點我升級為進階方案
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <!-- Line Section -->
        <template v-if="section === 'line'">
          <v-row :key="'line'" dense class="align-center">
            <v-col cols="12" md="3" class="bullet py-2">
              LINE 官方帳號
            </v-col>
            <v-col cols="12" md="9" class="py-2">
              <div>
                <v-radio-group
                  v-model="lineLink.line_page_status"
                  style="width: 100%;"
                  class="mt-0"
                  :disabled="!canEditLine"
                  hide-details="auto"
                >
                  <v-radio
                    label="不開放"
                    :value="0"
                  ></v-radio>
                  <v-radio
                    label="使用串接帳號（建議選項）"
                    :value="1"
                    @click="checkChannelExist"
                  ></v-radio>
                  <v-radio
                    label="自行輸入連結"
                    :value="2"
                  ></v-radio>
                  <v-text-field
                    v-model="lineLink.url"
                    :disabled="!(canEditLine && lineLink.line_page_status === 2)"
                    class="pl-6"
                    outlined
                    dense
                    placeholder="請輸入連結（注意：自行輸入將無法從小幫手系統追蹤來源）"
                    hide-details="auto"
                  >
                    <template #prepend-inner>
                      <v-icon class="px-1" color="grey">
                        {{ getSocialIcon(lineLink.label) }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-radio-group>
              </div>
            </v-col>
          </v-row>
        </template>

        <!-- Other Links Section -->
        <template v-if="section === 'others'">
          <div :key="'others'">
            <div v-for="link in socialLinks" :key="link.label">
              <v-row dense class="align-center">
                <v-col cols="12" md="3" class="bullet py-2">
                  {{ link.label }}
                </v-col>
                <v-col cols="12" md="9" class="py-2 d-flex flex-column" style="gap: 4px;">
                  <div>
                    <v-text-field
                      v-model="link.name"
                      dense
                      hide-details
                      placeholder="請輸入欲顯示的名稱，若無則顯示標題名稱"
                      outlined
                      :disabled="!canEditOthers"
                      clearable
                      maxlength="18"
                    />
                  </div>
                  <div>
                    <v-text-field
                      v-model="link.url"
                      dense
                      hide-details
                      :placeholder="'請輸入' + link.label + '連結'"
                      outlined
                      :disabled="!canEditOthers"
                    >
                      <template #prepend-inner>
                        <v-icon class="px-1" color="grey">
                          {{ getSocialIcon(link.label) }}
                        </v-icon>
                      </template>
                    </v-text-field>
                  </div>
                </v-col>
              </v-row>
            </div>
          </div>
        </template>

      </template>
    </div>

    <v-dialog v-model="lineAlertDialog" max-width="400">
      <v-card>
        <v-card-title>
          <div>
            您沒有串接的LINE官方賬號，請輸入其他連結，或是詢問 <a :href="$enum.DENTCO_CS_LINE" target="_blank">客服人員</a>
          </div>
        </v-card-title>
      </v-card>
    </v-dialog>
  </SettingLayout>
</template>

<script>
import SettingLayout from "./SettingLayout.vue";
import { getClinicInfoAPI, updateClinicInfoAPI, getMessageSettingAPI } from "@/api";
import { mapGetters, mapMutations } from "vuex";
import { getSocialIcon, checkPermissions } from "@/utils/tool";

export default {
  name: "ClinicSocialsV2",

  inject: ['getIsEdit', 'toggleEdit', 'toggleLoading', 'saveAlert'],

  components: { SettingLayout },

  props: {
    title: { required: true, type: String },
    subtitle: { required: true, type: String },
    userInfo: { required: true, type: Object }
  },

  data() {
    return {
      data: null,
      lineAlertDialog: false,
      defaultSocialLinksStructure: [
        { label: 'Instagram', name: '', url: '' },
        { label: 'Facebook', name: '', url: '' },
        { label: 'Tiktok', name: '', url: '' },
        { label: 'YouTube', name: '', url: '' },
        { label: '其他連結1', name: '', url: '' },
        { label: '其他連結2', name: '', url: '' },
        { label: '其他連結3', name: '', url: '' }
      ],
      socialLinks: [],
      defaultLineLink: {
        label: 'LINE',
        name: '',
        url: '',
        line_page_status: 0
      },
      lineLink: {}
    };
  },

  computed: {
    ...mapGetters(['axiosParamData', 'ownChannelLoaded', 'hasOwnChannel']),
    hasGuideLinePermission() {
      return checkPermissions({obj: this.userInfo, type: 'clinic', permissions: ['guide_to_line']});
    },
    hasAdvPagePermission() {
      return checkPermissions({obj: this.userInfo, type: 'clinic', permissions: ['adv_page']});
    },
    hasAnyEditPermission() {
      return this.hasAdvPagePermission || this.hasGuideLinePermission;
    },
    canEditLine() {
      return this.getIsEdit() && this.hasAnyEditPermission;
    },
    canEditOthers() {
      return this.getIsEdit() && this.hasAdvPagePermission;
    },
    contentOrder() {
      if (this.hasAdvPagePermission) {
        return ['line', 'others'];
      }
      if (this.hasGuideLinePermission) {
        return ['line', 'upgrade', 'others'];
      }
      return ['upgrade', 'line', 'others'];
    }
  },

  methods: {
    ...mapMutations({ setOwnChannel: 'setOwnChannel' }),
    getSocialIcon,
    cancel() {
      this.getData();
    },
    save() {
      this.toggleLoading(true);

      const social_item_structure = [this.lineLink, ...this.socialLinks].filter(link => {
        if (link.label === 'LINE') {
          return true;
        }

        // 其他連結照舊，沒有 url 就排除
        return !!link.url;
      });

      const payload = {
        ...this.axiosParamData,
        social_item_structure
      };
      updateClinicInfoAPI(payload).then(() => {
        this.getData();
        this.toggleEdit(false);
        this.saveAlert();
      }).catch(e => {
        alert('儲存失敗');
        console.error(e);
        this.toggleLoading(false);
      });
    },
    getData() {
      this.toggleLoading(true);
      getClinicInfoAPI(this.axiosParamData).then(res => {
        this.data = res.data;
        this.setData();
      }).finally(() => {
        this.toggleLoading(false);
      });
    },
    setData() {
      const socialStructure = this.data.social_item_structure;

      if (socialStructure && socialStructure.length) {
        const lineFromApi = socialStructure.find(link => link.label === 'LINE');
        const othersFromApi = socialStructure.filter(link => link.label !== 'LINE');

        this.lineLink = {
          ...this.defaultLineLink,
          ...lineFromApi
        };

        const apiLinksMap = new Map(othersFromApi.map(link => [link.label, link]));

        this.socialLinks = this.defaultSocialLinksStructure.map(defaultLink => {
          const foundLink = apiLinksMap.get(defaultLink.label);
          return foundLink ? { ...defaultLink, ...foundLink } : JSON.parse(JSON.stringify(defaultLink));
        });
      } else {
        // No API data at all. Use defaults.
        this.socialLinks = JSON.parse(JSON.stringify(this.defaultSocialLinksStructure));
        this.lineLink = JSON.parse(JSON.stringify(this.defaultLineLink));
      }
    },
    async checkChannelExist() {
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData);
        this.setOwnChannel(res.data.own_channel);
      }
      if (!this.hasOwnChannel) {
        this.lineAlertDialog = true;
        this.$nextTick(() => {
          this.lineLink.line_page_status = 0;
        });
      }
    }
  },

  mounted() {
    this.getData();
  }
};
</script>

<style scoped>
.bullet::before {
  content: "";
  background: #000000;
  padding-inline: 5px;
  height: 10px;
  margin-inline-end: 10px;
  display: inline-block;
}
</style>
